import { useState, useEffect } from 'react';
import { uploadAppendix4File, deleteAppendix4File, getAppendix4Files, updateProjectStep7, getProject } from '../../services/api';

const Step7DocumentUploads = ({ project, onComplete, onBack, isDCWMP = false, isCMP = false }) => {
  const [files, setFiles] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [maxFiles] = useState(10);
  const [maxFileSize] = useState(25 * 1024 * 1024); // 25MB

  useEffect(() => {
    loadFiles();
    // Load existing acknowledgement and file descriptions
    if (project) {
      setAcknowledged(project.appendix4_acknowledged || false);
      // Load file descriptions from project data
      setFileDescriptions(project.appendix4_file_descriptions || {});
    }
  }, [project]);

  const loadFiles = async () => {
    try {
      const data = await getAppendix4Files(project.id);
      setFiles(data.files);
    } catch (err) {
      console.error('Failed to load files:', err);
    }
  };

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/tiff', 'image/tif', 'image/png'];
    if (!validTypes.includes(file.type)) {
      return 'Invalid file type. Allowed: JPG, JPEG, PNG, TIFF';
    }
    if (file.size > maxFileSize) {
      return 'File too large. Maximum size is 25MB';
    }
    return null;
  };

  const handleFileUpload = async (filesToUpload) => {
    if (files.length >= maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setUploading(true);
    setError('');

    for (const file of filesToUpload) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        continue;
      }

      if (files.length >= maxFiles) {
        setError(`Maximum ${maxFiles} files reached`);
        break;
      }

      try {
        await uploadAppendix4File(project.id, file);
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.detail || 'Failed to upload file');
      }
    }

    await loadFiles();
    setUploading(false);
  };

  const handleDeleteFile = async (index) => {
    try {
      await deleteAppendix4File(project.id, index);
      await loadFiles();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.detail || 'Failed to delete file');
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    // Check if max files reached
    if (files.length >= maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileUpload(droppedFiles);
    }
  };

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      handleFileUpload(selectedFiles);
    }
    e.target.value = ''; // Reset input
  };

  const handleContinue = async () => {
    setError('');
    
    // Validate acknowledgement if files exist
    if (files.length > 0 && !acknowledged) {
      setError('Please acknowledge that uploaded documents will be included in Appendix D');
      return;
    }

    try {
      setSaving(true);
      
      // Build combined description from file descriptions (descriptions only, no filenames)
      const descriptionParts = files
        .map((file, index) => {
          const desc = fileDescriptions[file.filename];
          return desc ? desc : null;
        })
        .filter(Boolean);
      const combinedDescription = descriptionParts.join('\n');
      
      await updateProjectStep7(project.id, {
        appendix4_description: combinedDescription || null,
        appendix4_file_descriptions: fileDescriptions,
        appendix4_acknowledged: acknowledged
      });
      
      const updatedProject = await getProject(project.id);
      onComplete(updatedProject);
    } catch (err) {
      console.error('Failed to save Step 7:', err);
      setError(err.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    // Allow skipping if no files uploaded
    try {
      setSaving(true);
      await updateProjectStep7(project.id, {
        appendix4_description: null,
        appendix4_file_descriptions: {},
        appendix4_acknowledged: false
      });
      
      const updatedProject = await getProject(project.id);
      onComplete(updatedProject);
    } catch (err) {
      console.error('Failed to skip Step 7:', err);
      setError(err.response?.data?.detail || 'Failed to continue. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Appendix D – Supporting Documentation</h2>
        <p className="mt-1 text-sm text-gray-600">
          Upload supporting documents and images that provide additional context to your Waste Management Plan.
        </p>
      </div>

      <div className="px-8 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Purpose Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Purpose of this Section</h3>
          <p className="text-sm text-blue-800">
            Supporting documentation is <strong>optional</strong>, but may assist Council, certifiers, or waste contractors 
            in understanding site-specific constraints or operational considerations. Uploaded files will be included 
            in Appendix D of the final WMP submission.
          </p>
        </div>

        {/* What You Can Upload */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">What You Can Upload</h3>
          </div>
          <div className="p-4">
            {/* Highlighted Important Items for Council - Only show for OpWMP */}
            {!isDCWMP && !isCMP && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="ml-3">
                    <h4 className="text-sm font-bold text-amber-800 mb-2">Important for Council Assessment</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <span className="text-amber-600 font-bold mr-2">★</span>
                        <span className="text-sm font-medium text-amber-900">
                          Pathway from the Waste Generation Points to the Bin Storage Area 
                          <span className="text-amber-700 font-normal"> (indicated on Architectural plans)</span>
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-amber-600 font-bold mr-2">★</span>
                        <span className="text-sm font-medium text-amber-900">
                          Pathway from the Bin Storage Area to the Waste Collection Point 
                          <span className="text-amber-700 font-normal"> (indicated on Architectural plans)</span>
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-600 mb-3">You may also upload any other relevant documents or images, including:</p>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
              <div className="flex items-center">
                <span className="text-blue-500 mr-2">•</span>
                Swept path diagrams
              </div>
              <div className="flex items-center">
                <span className="text-blue-500 mr-2">•</span>
                Waste room or bin store layouts
              </div>
              <div className="flex items-center">
                <span className="text-blue-500 mr-2">•</span>
                Site plans or marked-up drawings
              </div>
              <div className="flex items-center">
                <span className="text-blue-500 mr-2">•</span>
                HazMat or contamination reports
              </div>
              <div className="flex items-center">
                <span className="text-blue-500 mr-2">•</span>
                Waste audits or studies
              </div>
              <div className="flex items-center">
                <span className="text-blue-500 mr-2">•</span>
                Contractor correspondence
              </div>
              <div className="flex items-center">
                <span className="text-blue-500 mr-2">•</span>
                Photos of site conditions
              </div>
              <div className="flex items-center">
                <span className="text-blue-500 mr-2">•</span>
                Any other supporting material
              </div>
            </div>
          </div>
        </div>

        {/* File Upload Area */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Upload Your Files</h3>
            <span className="text-sm text-gray-500">
              {files.length} / {maxFiles} files
            </span>
          </div>
          
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragging 
                ? 'border-blue-500 bg-blue-50' 
                : files.length >= maxFiles
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'border-gray-300 hover:border-blue-400'
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Uploading...</p>
              </div>
            ) : (
              <>
                <svg
                  className={`mx-auto h-12 w-12 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`}
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="mt-4">
                  <label
                    htmlFor="file-upload"
                    className={`relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 ${
                      files.length >= maxFiles ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <span>Click to upload</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept=".jpg,.jpeg,.tiff,.tif,.png"
                      multiple
                      onChange={handleFileInputChange}
                      disabled={files.length >= maxFiles}
                      className="sr-only"
                    />
                  </label>
                  <span className="text-gray-500"> or drag and drop</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  JPG, JPEG, PNG, TIFF up to 25MB each (max {maxFiles} files)
                </p>
              </>
            )}
          </div>
        </div>

        {/* Uploaded Files List with Inline Descriptions */}
        {files.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Uploaded Documents</h3>
            <p className="text-xs text-gray-500 mb-3">
              Add a brief description to each document to help reviewers understand its purpose.
            </p>
            <div className="space-y-3">
              {files.map((file, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 font-semibold text-sm mr-3 flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-medium text-gray-900 truncate">{file.filename}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteFile(index)}
                      className="ml-4 text-red-600 hover:text-red-800 text-sm font-medium flex-shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="ml-11">
                    <input
                      type="text"
                      placeholder="e.g., Swept path diagram for basement access"
                      value={fileDescriptions[file.filename] || ''}
                      onChange={(e) => setFileDescriptions(prev => ({
                        ...prev,
                        [file.filename]: e.target.value
                      }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      maxLength={150}
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Optional description ({(fileDescriptions[file.filename] || '').length}/150)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Important Notes */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h4 className="text-sm font-semibold text-amber-800">Important Notes</h4>
              <ul className="mt-1 text-sm text-amber-700 space-y-1">
                <li>• Uploaded files are not reviewed or verified for technical accuracy</li>
                <li>• They are included as provided, without modification</li>
                <li>• Uploading documents does not replace required plans or reports</li>
                <li>• Councils may still request additional information after submission</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Acknowledgement Checkbox - Only show if files uploaded */}
        {files.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900">
                  I acknowledge that uploaded documents will be included in Appendix D of the Waste Management Plan as supporting information only.
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  By uploading files, you confirm that you have the right to share these documents and that the information is accurate to the best of your knowledge.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </button>
          <div className="flex gap-3">
            {files.length === 0 && (
              <button
                type="button"
                onClick={handleSkip}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Skip This Step
              </button>
            )}
            <button
              type="button"
              onClick={handleContinue}
              disabled={saving || (files.length > 0 && !acknowledged)}
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Continue to Review & Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step7DocumentUploads;
